/* eslint-disable camelcase */
/**
 * @module Auth0NewUserOnboardingHelper
 * @description This is a wrapper class designed to organize and expose existing functionality that is helpful to clients that are exposing new user onboarding functionality. As such, it's unlikely to contain any of its own logic.
 */
/* eslint-disable import/extensions */
import Auth0SupportedIdps from '../utils/auth0-supported-idps.mjs';
import Auth0SsoButtonHelper from '../utils/auth0-sso-button-helper.mjs';
import Auth0Utils from '../utils/auth0-utils-browser.mjs';
import Auth0CookieHelper from '../utils/auth0-cookie-helper.mjs';
import Auth0Localizer from '../utils/auth0-localization-helper.mjs';
import Auth0Constants from '../utils/auth0-constants.mjs';
import Auth0WaitSpinner from './pages/auth0-wait-spinner-page-helper.mst.mjs';
import logger from '../utils/logging/auth0-logger.mjs';

class Auth0NewUserOnboardingHelper {
  /**
   * @constructor
   * @description This package should only ever be used by the [Auth0-hosted login page](./templates/pages/login.html.mst). It controls white label display properties, captcha widget loading, SSO button rendering and SSO login to selected Identity Providers via popup. Using it requires on that it be loaded and a new object of type `Auth0LoginPageHelper` be instantiated. Because it is an ES6 module, you'll need to use the `import` directive in a `script` block of type `module`. Note that each tenant hosts its own `auth0-login-page-helper` package and, as such, the `import` directive must be dynamic.
   * @param {string} auth0ConfigObj Auth0-provided options string
   * @example <caption>The `Auth0LoginPageHelper` constructor takes only one arg. Specifically, the literal string `@@config@@` must be passed in. This is a token replacement variable that Auth0 servers will use when they render the hosted login page.</caption>
   * <script type="text/javascript" src="https://cdn.shipstation.com/auth0/shipstation-stage/auth0.ssoOnboarding.min.js?cb=${cb}"></script>
   * <script type="text/javascript">
   *  const onboardHelper = new auth0Helper.ssoOnboarding.Auth0NewUserOnboardingHelper({ options });
   * </script>
   */
  constructor(options) {
    this._initLogger();

    if (!options.ssoAuthenticationUrl) {
      throw new Error('Auth0NewUserOnboardingHelper instantiation has failed because options.ssoAuthenticationUrl is missing (this is the URL of the auctane app that handles login requests; e.g. v3 is https://ship.shipstation.com/loginauth)');
    }
    // verify that the requested sso auth url is on same domain as this page or cookie will not travel
    let ssoUrl;
    try {
      ssoUrl = new URL(options.ssoAuthenticationUrl);
    } catch (error) {
      throw new Error(`startOnboardingFlow has failed because options.ssoAuthenticationUrl (${options.ssoAuthenticationUrl}) is not a valid URL (this is the URL of the auctane app that handles login requests; e.g. v3 is https://ship.shipstation.com/loginauth) => ${error}`);
    }

    const ssoTld = Auth0Utils.getTopLevelDomain(ssoUrl);
    const thisTld = Auth0Utils.getTopLevelDomain(window.location.hostname);
    if (ssoTld !== thisTld) {
      throw new Error(`startOnboardingFlow has failed because options.ssoAuthenticationUrl (${options.ssoAuthenticationUrl}) is on domain ${ssoTld} and the current domain is ${thisTld}. These domains must match.`);
    }

    this._tenant = options.tenant;
    this._supportedSsoIdpsHelper = new Auth0SupportedIdps({
      tenantOrDomain: options.tenant,
      appLoginHandlerUrl: options.ssoAuthenticationUrl,
    });
    this._ssoButtonHelper = new Auth0SsoButtonHelper({
      tenantOrDomain: options.tenant,
      appLoginHandlerUrl: options.ssoAuthenticationUrl,
    });
  }

  _initLogger = () => {
    logger.initialize({
      sumoEndpoint: 'https://endpoint2.collection.us2.sumologic.com/receiver/v1/http/ZaVnC4dhaV1uAI96duDvnKsBH-5zBCQS4Z9yRC6qyLXrEdfezmbhON_4so37Qor_6qM1eto00rRWjhlaqRccMgcVRvrLAKRfcyNBdeziv7B_grZ3H8sjew==',
      sourceName: 'Auth0NewUserOnboardingHelper',
    });
  };

  /**
   * @property {array} supportedIdps
   * @description A list of supported IDPs that can be iterated for custom rendering of SSO buttons
   * @readonly
   * @example <caption>An example Identity Provider Entry</caption>
   * {
   *    name: 'Google',
   *    id: 'google-oauth2',
   *    logoUrl: 'https://cdn.shipstation.com/auth0/shipstation/public/images/sso-icon-google.png',
   * }
   */
  get supportedIdps() {
    return this._supportedSsoIdpsHelper.supportedIdps;
  }

  /**
   * @property {array} activeIdps
   * @description A list of active IDPs that can be iterated for custom rendering of SSO buttons (a subset of supportedIdps)
   * @readonly
   * @example <caption>An example Identity Provider Entry</caption>
   * {
   *    name: 'Google',
   *    id: 'google-oauth2',
   *    logoUrl: 'https://cdn.shipstation.com/auth0/shipstation/public/images/sso-icon-google.png',
   * }
   */
  get activeIdps() {
    return this._supportedSsoIdpsHelper.activeIdps;
  }

  /**
   * @property {Auth0SupportedIdps} idpHelper
   * @description Access to an instance of an Auth0SupportedIdps
   * @readonly
   */
  get idpHelper() {
    return this._supportedSsoIdpsHelper;
  }

  /**
   * @function
   * @description A helper function that wil generate the SSO button list and then inject them into the user-specified container
   * @param {object} options Button generation options
   * @param {HTMLElement|string} options.container The container into which the SSO buttons should be injected. Can be an HTMLElement or container ID.
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {array} [options.buttonClassList] The optional list of classes to apply to each of the SSO buttons
   */
  injectSsoButtonSet = (options) => {
    this._ssoButtonHelper.injectSsoButtonSet(options);
  };

  /**
   * @function
   * @description A helper function that wil generate the SSO button list within a single node (div) container
   * @param {object} options Button generation options
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {string} [options.containerId] The optional ID of the div container
   * @param {array} [options.classList] The optional list of classes to apply to the div container
   * @param {array} [options.buttonClassList] The optional list of classes to apply to each of the SSO buttons
   * @param {boolean} [options.generateAsHtmlText=false] Optional. If false (default), this function will return a standard `HTMLElement` object containing the buttons. Otherwise, it will return the equivalent HTML text
   * @returns {HTMLElement|string} The single node (div) containing the SSO buttons. This object has not been added to the DOM.
   */
  generateSsoButtonSet = (options) => {
    return this._ssoButtonHelper.generateSsoButtonSet(options);
  };

  /**
   * @function
   * @description A helper function that wil generate a single SSO button for the provided IdP
   * @param {object|string} socialIdp The IdP for which to generate a button
   * @param {object} options Button generation options
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {array} [options.buttonClassList] The optional list of classes to apply to the SSO button
   * @param {boolean} [options.generateAsHtmlText=false] Optional. If false (default), this function will return a standard `HTMLElement` object containing the button. Otherwise, it will return the equivalent HTML text
   * @returns {HTMLElement|string} The single node (div) containing the SSO button for the specified IDP. This object has not been added to the DOM.
   */
  generateSsoButton = (socialIdp, options) => {
    return this._ssoButtonHelper.generateSsoButton(socialIdp, options);
  };

  /**
   * @function
   * @description A helper function that starts the signin flow for an IdP (login flow differs based on IDP settings) for purposes of creating a new SP user
   * @param {string} options User onboarding options
   * @param {string} options.idpId The Auth0-based ID of the Identity Provider for which to start the signin flow
   * @param {string} options.ioBlackBoxToken Anti-fraud Iovation BlackBox token. See SS marketing site for example generation
   * @param {string} options.captchaToken The captcha token is required if the `registration_channel`. See {@link https://app.launchdarkly.com/default/production/features/enable-captcha-validation-for-ship-station-registration-channel/targeting}. Currently, this token must be Google reCaptcha v3 type.
   * @param {string} [options.ipAddress] Optional. Indicate the IP address from where the SSO signup button was clicked.
   * @param {string} [options.registrationChannel] The captcha token is required if the `registrationChannel` is not present in the following LD flag. See {@link https://app.launchdarkly.com/default/production/features/enable-captcha-validation-for-ship-station-registration-channel/targeting}. Currently, this token must be Google reCaptcha v3 type.
   * @param {string} [options.hubSpotCookie] The optional tracking cookie used for HubSpot correlation.
   * @param {string} [options.requestedLanguage] Optional. If missing, language will be inferred by data in the IDP profile
   * @param {string} [options.appRedirectUrl] The Auctane app URL to which Auth0 should redirect. This arg is an override. If none is supplied, this will default to using this._appLoginHandlerUrl
   */
  startSsoOnboardingFlow = async (options) => {
    console.log(`Auth0NewUserOnboardingHelper.startOnboardingFlow => ${JSON.stringify(options)}`);
    await this._showSpinner();

    // NOTE: the marketing onboard page is the only page that calls this atm... if we add others, be sure to differentiate Auth0Constants.AUTH0_IDP_SIGNIN_CONTEXT_VAL_MARKETING_PAGE

    // validate
    if (!options.ioBlackBoxToken) {
      throw new Error(`${Auth0Localizer.getLocalizedText('error-create-user-code', { code: Auth0Constants.ERROR_CODE_CREATE_USER_BAD_REQUEST })} - Missing BlackBox security token.`);
    }
    if (!options.captchaToken) {
      throw new Error(`${Auth0Localizer.getLocalizedText('error-create-user-code', { code: Auth0Constants.ERROR_CODE_CREATE_USER_BAD_REQUEST })} - Missing Google Captcha token.`);
    }
    try {
      await this._supportedSsoIdpsHelper.startIdpOnboardingLogin(options);
    } catch (error) {
      // likely a deep problem if we fail here so we'll rethrow a user-presentable error message in its place.
      logger.error(`Failed in startSsoOnboardingFlow! ${error.message || error}`);
      throw new Error(Auth0Localizer.getLocalizedText('error-create-user-code', { code: Auth0Constants.ERROR_CODE_CREATE_USER_UNKNOWN }));
    }
  };

  /**
   * @function
   * @description A function that is run for all new users after successful onboarding at the time of first login. For custom onboarding actions by IdP, see `Auth0IdentityProvider.runNewUserOnboardingActions()`. Currently, this function will assemble an object containing data that is useful for new user tracking (sales) and then redirect to marketing site where this data will be extracted and processed. The marketing site is responsible for redirecting to new user home environment.
   * @param {object} newUserData A standard auth0 user object with the database response in `app_metadata.registrationResponse` (only the custom `registrationResponse` prop is documented here, but all standard auth0 user props are present)
   * @param {object} newUserData.userData Any properties given to Auth0 by the IdP after signin (varies by IdP)
   * @param {object} newUserData.registrationResponse The response from the database wrt the request to create a new user
   * @param {string} newUserData.registrationResponse.userId The new user's ID
   * @param {string} newUserData.registrationResponse.userName The new user's username
   * @param {string} newUserData.registrationResponse.host The new user's host partition
   * @param {number} newUserData.registrationResponse.trialEndDate The new user's trial end date
   * @param {boolean} newUserData.registrationResponse.verified Not sure what this means... would always be false for new users???
   * @param {string} [newUserData.registrationResponse.refno] The new user's referral code (if any)
   * @param {boolean} [newUserData.registrationResponse.isFraud] Not sure how this is determined??? it comes from WebAPI
   */
  static processNewUserRegistrationResponseActions = async (newUserData) => {
    if (!newUserData) {
      console.log('Auth0NewUserOnboardingHelper.processNewUserRegistrationResponseActions => no data available, skipping...');
      return;
    }

    console.log(`Auth0NewUserOnboardingHelper.processNewUserRegistrationResponseActions => ${JSON.stringify(newUserData)}`);
    // console.log('Auth0NewUserOnboardingHelper.processNewUserRegistrationResponseActions start...');
    const { registrationResponse, userData } = newUserData;
    const { user_metadata: idpMetadata } = userData.idpProps;
    const userClaimSet = Auth0Utils.getUserClaimSet(userData);

    const userTrackingObj = {
      auth0UserId: userData.sub,
      userId: userData.sub.replace(Auth0Constants.AUTH0_ID_PREFIX, ''),

      // Standard OAUTH2 props
      name: userData.name,
      nickname: userData.nickname,
      givenName: userData.given_name,
      familyName: userData.family_name,
      email: userData.email,
      emailVerified: userData.email_verified,
      phone: userData.phone,
      phoneVerified: userData.phone_verified,
      picture: userData.picture,

      // SS props
      username: userClaimSet.username,
      sellerId: userClaimSet.seller_id,
      host: userClaimSet.partition_host,
      homeCountry: userClaimSet.seller_home_country,

      // SS ProxyRegistration props
      registerSuccess: registrationResponse?.success,
      verified: registrationResponse?.verified,
      partitionId: registrationResponse?.partitionId,
      refno: registrationResponse?.refno,
      isFraud: registrationResponse?.isFraud,
      couponCode: registrationResponse?.couponCode,
      trialEndDate: registrationResponse?.trialEndDate,

      // chosen onboardingVariant for progressive disclosure
      onboardingVariant: idpMetadata?.onboardingVariant,

      // instructions to marketing site on where it should redirect when done
      // (consider DDEs here as well so we can end to end test in those envs)
      redirectUri: this._getMarketingRedirectUrl(newUserData),
    };
    console.log(`Auth0NewUserOnboardingHelper.processNewUserRegistrationResponseActions => built tracking object => ${JSON.stringify(userTrackingObj)}`);

    Auth0CookieHelper.setCookieValueWithExpirationInSeconds(
      Auth0Constants.AUTH0_NEW_USER_TRACKING_DATA,
      JSON.stringify(userTrackingObj),
      Auth0Constants.GLOBAL_FLOW_TIMEOUT_SEC,
    );
  };

  static _getMarketingRedirectUrl = (newUserData) => {
    const { userData } = newUserData;
    const userClaimSet = Auth0Utils.getUserClaimSet(userData);
    let url = Auth0Utils.specialRedirectFilterForDevEnv(userClaimSet.partition_host); // considers DDE support
    url = url.startsWith('http') ? url : `https://${url}`;
    console.log(`Auth0NewUserOnboardingHelper._getMarketingRedirectUrl derived redirectUri => ${url}`);
    return url;
  };

  /**
   * @function
   * @description A function that is run for all new users after successful onboarding. For custom onboarding actions by IdP, see `Auth0IdentityProvider.runNewUserOnboardingActions()`.
   * @param {string} newUserData A standard auth0 user object
   */
  static runNewUserOnboardingActions = async (newUserData) => {
    console.log(`Auth0NewUserOnboardingHelper.runNewUserOnboardingActions => ${JSON.stringify(newUserData)}`);
  };

  _showSpinner = async (options = {}) => {
    await new Auth0WaitSpinner({
      name: options.parent || 'SsoOnboardWaitSpinner',
      waitTextOverrideKey: options.waitTextOverrideKey,
      waitTextReplacementArgs: options.waitTextReplacementArgs,
      tenantOrDomain: this._tenant,
      whiteLabelOptions: this._whiteLabelOptions,
    }).render();
  };
}

export { Auth0NewUserOnboardingHelper };
